import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../layouts/Header';
import axiosInstance from '../../api/axios';
import OutletSelect from '../../components/OutletSelect'; // adjust path to match your project

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const currencyFmt = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
});

const numFmt = new Intl.NumberFormat('en-IN');

const fmtCurrency = (val) => currencyFmt.format(Number(val) || 0);
const fmtNum = (val) => numFmt.format(Number(val) || 0);
const fmtPercent = (val) => `${(Number(val) || 0).toFixed(2)}%`;

// monthlySummary.summary fields come back as { $numberDecimal: "..." } for money fields
const decimal = (field) => {
    if (field === null || field === undefined) return 0;
    if (typeof field === 'object' && '$numberDecimal' in field) return parseFloat(field.$numberDecimal) || 0;
    return parseFloat(field) || 0;
};

const CATEGORY_COLORS = {
    FOOD: { bg: '#ecfdf3', border: '#d5f5e2', text: '#15803d' },
    BEVERAGE: { bg: '#eef4ff', border: '#d8e4ff', text: '#2563eb' },
    DESSERTS: { bg: '#fff7ed', border: '#ffedd5', text: '#ea580c' },
    COMBOS: { bg: '#f5f3ff', border: '#ede9fe', text: '#7c3aed' },
    EXTRAS: { bg: '#f0f9ff', border: '#e0f2fe', text: '#0284c7' },
};
const categoryStyle = (cat) => CATEGORY_COLORS[cat] || { bg: '#f3f4f6', border: '#e5e7eb', text: '#374151' };

const KpiCard = ({ label, value, sub, bg, border, color }) => (
    <div className="msr-kpi-card" style={{ background: bg, borderColor: border }}>
        <div className="msr-kpi-label">{label}</div>
        <div className="msr-kpi-value" style={{ color }}>{value}</div>
        {sub && <div className="msr-kpi-sub">{sub}</div>}
    </div>
);

// ─── Embedded styles (kept inline so nothing lives outside this file) ──────
const MonthSummaryReportStyles = () => (
    <style>{`
        .msr-page { max-width: 1200px; margin: 0 auto; background: #dfeef4; }

        /* ─── Hero banner (brand-blue, matches email header) ─── */
        .msr-hero {
            background: linear-gradient(135deg, #09A5F7 0%, #0b8fe0 100%);
            border-radius: 20px 20px 0 0;
            padding: 26px 30px 60px 30px;
            position: relative;
            color: #ffffff;
        }
        .msr-hero-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 18px;
        }
        .msr-back-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: rgba(255,255,255,0.16);
            border: 1px solid rgba(255,255,255,0.35);
            color: #ffffff;
            font-size: 13px;
            font-weight: 600;
            padding: 7px 14px;
            border-radius: 20px;
            cursor: pointer;
            transition: background 0.15s ease;
        }
        .msr-back-btn:hover { background: rgba(255,255,255,0.28); }
        .msr-back-btn i { font-size: 14px; }

        .msr-hero-body { display: flex; align-items: center; gap: 16px; }
        .msr-hero-icon {
            width: 52px;
            height: 52px;
            border-radius: 50%;
            background: rgba(255,255,255,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            flex-shrink: 0;
        }
        .msr-title { font-size: 26px; font-weight: 700; margin: 0; color: #ffffff; }
        .msr-subtitle { font-size: 14px; color: rgba(255,255,255,0.9); margin: 4px 0 0; }

        /* ─── Filter card (overlaps hero, like the email's meta box) ─── */
        .msr-filter-card {
            background: #ffffff;
            border-radius: 16px;
            box-shadow: 0 8px 24px rgba(9,165,247,0.15);
            margin: -42px 24px 0 24px;
            padding: 20px 24px;
            position: relative;
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            align-items: flex-end;
        }
        .msr-filter-item { min-width: 150px; flex: 1; }
        .msr-outlet-filter { min-width: 260px; flex: 2; }
        .msr-filter-label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 4px; color: #374151; }

        /* ─── Download report button ─── */
        .msr-download-item { flex: 0 0 auto; min-width: 0; }
        .msr-download-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            background: #15803d;
            border: none;
            color: #ffffff;
            font-size: 14px;
            font-weight: 600;
            padding: 0 18px;
            height: 38px;
            border-radius: 10px;
            cursor: pointer;
            white-space: nowrap;
            transition: background 0.15s ease;
        }
        .msr-download-btn:hover:not(:disabled) { background: #12692f; }
        .msr-download-btn:disabled { background: #9ca3af; cursor: not-allowed; }
        .msr-download-btn .spinner-border-sm { width: 0.9rem; height: 0.9rem; }

        .msr-body { padding: 30px 24px 10px 24px; background: #dfeef4; border-radius: 0 0 20px 20px; }

        .msr-card {
            background: #ffffff;
            border: 1px solid #d9dee5;
            border-radius: 14px;
            padding: 20px 22px;
            margin-bottom: 20px;
        }
        .msr-card h2 { font-size: 18px; margin: 0 0 14px; color: #202124; }
        .msr-card-header-row { display: flex; justify-content: space-between; align-items: center; }

        .msr-exec-summary p { font-size: 14px; line-height: 24px; color: #475467; margin: 0; }

        .msr-kpi-grid { display: grid; gap: 14px; margin-bottom: 20px; }
        .msr-kpi-grid-2 { grid-template-columns: repeat(2, 1fr); }
        .msr-kpi-grid-3 { grid-template-columns: repeat(3, 1fr); }
        .msr-kpi-grid-4 { grid-template-columns: repeat(4, 1fr); }

        .msr-kpi-card { border: 1px solid; border-radius: 14px; padding: 16px 18px; }
        .msr-kpi-label { font-size: 13px; color: #5f6368; }
        .msr-kpi-value { font-size: 24px; font-weight: 800; margin-top: 6px; }
        .msr-kpi-sub { font-size: 12px; color: #6b7280; margin-top: 6px; }

        .msr-platform-card, .msr-coupon-card, .msr-topper-card, .msr-product-box {
            border: 1px solid #e5e7eb;
            border-radius: 14px;
            padding: 18px;
        }
        .msr-platform-card { background: #fff7ed; border-color: #ffedd5; }
        .msr-platform-name { font-size: 16px; font-weight: 800; color: #ea580c; }
        .msr-platform-orders { font-size: 22px; font-weight: 800; margin-top: 6px; }
        .msr-platform-row { font-size: 13px; color: #6b7280; margin-top: 6px; }

        .msr-coupon-card { background: #f0fdf4; border-color: #dcfce7; }
        .msr-coupon-code { font-size: 15px; font-weight: 800; color: #15803d; word-break: break-word; }
        .msr-coupon-usage { font-size: 12px; color: #6b7280; margin-top: 4px; }
        .msr-coupon-row { font-size: 13px; margin-top: 8px; }

        .msr-topper-label { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; }
        .msr-topper-name { font-size: 17px; font-weight: 800; color: #111827; margin-top: 8px; }
        .msr-topper-sub { font-size: 12px; color: #6b7280; margin-top: 4px; }

        .msr-two-col { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
        .msr-product-box-top { background: #ecfdf3; border-color: #d5f5e2; }
        .msr-product-box-bottom { background: #fff1f2; border-color: #ffe4e6; }
        .msr-product-box h3 { font-size: 15px; margin: 0 0 10px; }
        .msr-product-box ul { list-style: none; padding: 0; margin: 0; }
        .msr-product-box li { font-size: 14px; line-height: 26px; color: #202124; }
        .msr-product-count { color: #6b7280; }

        .msr-table-scroll { overflow-x: auto; }
        .msr-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .msr-table thead tr { background: #09A5F7; }
        .msr-table thead th { color: #fff; text-align: left; padding: 12px 16px; white-space: nowrap; }
        .msr-table tbody td { padding: 12px 16px; border-top: 1px solid #e5e7eb; white-space: nowrap; }
        .msr-table tbody tr:nth-child(even) { background: #fbfbfc; }
        .msr-table .text-end { text-align: right; }

        .msr-pagination { display: flex; align-items: center; gap: 10px; font-size: 13px; }

        .msr-insights ul { list-style: none; padding: 0; margin: 0 }
        .msr-insights li { font-size: 14px; line-height: 28px; color: #475467; }

        .msr-text-green { color: #15803d; }
        .msr-text-red { color: #dc2626; }

        .msr-empty-state, .msr-loading-state, .msr-error-state {
            text-align: center;
            padding: 60px 20px;
            color: #6b7280;
        }
        .msr-empty-state i, .msr-error-state i { font-size: 32px; display: block; margin-bottom: 10px; }

        @media (max-width: 768px) {
            .msr-kpi-grid-2, .msr-kpi-grid-3, .msr-kpi-grid-4, .msr-two-col { grid-template-columns: 1fr; }
            .msr-hero-top { flex-wrap: wrap; }
            .msr-filter-card { margin: -30px 12px 0 12px; flex-direction: column; align-items: stretch; }
            .msr-download-item { width: 100%; }
            .msr-download-btn { width: 100%; }
            .msr-body { padding: 24px 12px 0 12px; }
        }
    `}</style>
);

const MonthSummaryReport = () => {
    const navigate = useNavigate();
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = React.useState(currentSkin);
    const accessToken = localStorage.getItem('authToken');

    const now = new Date();
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [page, setPage] = useState(1);
    const limit = 50;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [topProducts, setTopProducts] = useState([]);
    const [bottomProducts, setBottomProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryToppers, setCategoryToppers] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [onlinePlatforms, setOnlinePlatforms] = useState([]);
    const [foodDeliveryAppsSales, setFoodDeliveryAppsSales] = useState({});
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

    const getMonthSummaryReport = useCallback(async () => {
        if (!selectedOutlet?.value) return;
        setLoading(true);
        setError(null);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get('order/monthlyOrderSummary/new', {
                headers,
                params: { outletId: selectedOutlet.value, year, month, page, limit },
            });

            if (response?.data?.status === true) {
                const d = response.data.data || {};
                setReportData(d.reportData || []);
                setSummary(d.monthlySummary?.summary || null);
                setTopProducts(d.monthlySummary?.topProducts || []);
                setBottomProducts(d.monthlySummary?.bottomProducts || []);
                setCategories(d.monthlySummary?.categories || []);
                setCategoryToppers(d.monthlySummary?.categoryToppers || []);
                setCoupons(d.monthlySummary?.coupons || []);
                setOnlinePlatforms(d.monthlySummary?.onlinePlatforms || []);
                setFoodDeliveryAppsSales(d.foodDeliveryAppsSales || {});
            } else {
                setError('Failed to load report data.');
            }
        } catch (err) {
            console.log('Error in Fetching Data of Month Summary Report :', err);
            const status = err?.response?.status;
            const msg = status === 406
                ? err?.response?.data?.error?.message || err?.response?.data?.error
                : status === 412
                    ? err?.response?.data?.data?.message
                    : err?.response?.data?.error || 'Failed to fetch month summary report';
            setError(typeof msg === 'string' ? msg : 'Failed to fetch month summary report');
        } finally {
            setLoading(false);
        }
    }, [selectedOutlet, year, month, page, accessToken]);

    // ─── Download Excel report ──────────────────────────────────────────
    const handleDownloadExcel = async () => {
        if (!selectedOutlet?.value) return;
        setDownloadBtnLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            };
            const response = await axiosInstance.get('order/monthlyOrderSummary/new', {
                headers,
                params: {
                    outletId: selectedOutlet.value,
                    year,
                    month,
                    page,
                    limit,
                    isDownload: true,
                },
                responseType: 'blob',
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            const fileName = `[${selectedOutlet?.label}] Summary Report ${MONTH_NAMES[month - 1]} ${year}.xlsx`;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
        } catch (err) {
            console.log('Error downloading Month Summary Report :', err);
            const status = err?.response?.status;
            const msg = status === 406
                ? err?.response?.data?.error?.message || err?.response?.data?.error
                : status === 412
                    ? err?.response?.data?.data?.message
                    : err?.response?.data?.error || 'Failed to download report';
            setError(typeof msg === 'string' ? msg : 'Failed to download report');
        } finally {
            setDownloadBtnLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [selectedOutlet, year, month]);

    useEffect(() => {
        getMonthSummaryReport();
    }, [getMonthSummaryReport]);

    // ─── Derived metrics ────────────────────────────────────────────────

    const totalOrders = summary?.totalOrderCount || 0;
    const totalInvoices = summary?.totalInvoiceCount || 0;
    const cancelledOrders = summary?.totalCancelledOrderCount || 0;
    const cancelledAmount = decimal(summary?.cancelledOrderAmount);
    const taxableAmount = decimal(summary?.totalTaxableAmount);
    const discountAmount = decimal(summary?.totalDiscountAmount);
    const taxAmount = decimal(summary?.totalTaxAmount);
    const grandTotal = decimal(summary?.grandTotalAmount);
    const grossSales = taxableAmount + discountAmount;

    const completedOrders = Math.max(totalOrders - cancelledOrders, 0);
    const aov = completedOrders > 0 ? taxableAmount / completedOrders : 0;

    // cancellationRate & revenueLoss now come pre-computed from the API,
    // instead of being calculated on the frontend
    const cancellationRate = summary?.cancellationRate ?? 0;
    const revenueLossPercent = summary?.revenueLoss ?? 0;
    const discountPercent = grossSales > 0 ? (discountAmount / grossSales) * 100 : 0;

    const couponOrders = summary?.ordersWithCouponCount || 0;
    const nonCouponOrders = summary?.ordersWithoutCouponCount || 0;
    const couponUsageRate = totalOrders > 0 ? (couponOrders / totalOrders) * 100 : 0;

    const kioskOrders = summary?.totalKioskOrderCount || 0;
    const onlineOrdersTotal = onlinePlatforms.reduce((sum, p) => sum + (p.orders || 0), 0);
    const otherOrders = Math.max(totalOrders - kioskOrders - onlineOrdersTotal, 0);

    const complimentaryOrders = decimal(summary?.totalComplimentaryOrderCount);
    const complimentaryAmount = decimal(summary?.complimentaryTaxableAmount);

    const categoryRevenueTotal = categories.reduce((sum, c) => sum + decimal(c.revenue), 0);

    const taxSlabs = [
        { slab: '5%', cgst: decimal(summary?.cgst5PercentAmount), sgst: decimal(summary?.sgst5PercentAmount) },
        { slab: '12%', cgst: decimal(summary?.cgst12PercentAmount), sgst: decimal(summary?.sgst12PercentAmount) },
        { slab: '18%', cgst: decimal(summary?.cgst18PercentAmount), sgst: decimal(summary?.sgst18PercentAmount) },
        { slab: '28%', cgst: decimal(summary?.cgst28PercentAmount), sgst: decimal(summary?.sgst28PercentAmount) },
    ].filter((s) => s.cgst > 0 || s.sgst > 0);

    // topProducts, bottomProducts, and categoryToppers now come pre-computed
    // from the API — no need to sort/derive them on the frontend anymore

    const topCoupons = [...coupons]
        .sort((a, b) => decimal(b.revenue) - decimal(a.revenue))
        .slice(0, 3);

    // foodDeliveryAppsSales (swiggy / zomato) is a new top-level field.
    // Currently unused in the UI below until we confirm the populated shape.
    const hasFoodDeliveryData =
        foodDeliveryAppsSales &&
        (foodDeliveryAppsSales.swiggy != null || foodDeliveryAppsSales.zomato != null);

    // ─── Render ─────────────────────────────────────────────────────────

    return (
        <React.Fragment>
            <MonthSummaryReportStyles />
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>

                <div className="msr-page">

                    {/* ─── Hero banner ─── */}
                    <div className="msr-hero">
                        <div className="msr-hero-top">
                            <button
                                type="button"
                                className="msr-back-btn"
                                onClick={() => navigate('/reports/all-reports')}
                            >
                                <i className="bi bi-arrow-left"></i>
                                Back to Reports
                            </button>
                        </div>

                        <div className="msr-hero-body">
                            <div className="msr-hero-icon">📊</div>
                            <div>
                                <h1 className="msr-title">Monthly Outlet Summary Report</h1>
                                <p className="msr-subtitle">
                                    {selectedOutlet
                                        ? `Performance overview for ${selectedOutlet.label}`
                                        : 'Select an outlet to view its monthly summary'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ─── Filter card (overlaps hero) ─── */}
                    <div className="msr-filter-card">
                        <div className="msr-filter-item msr-outlet-filter">
                            <OutletSelect
                                value={selectedOutlet}
                                onChange={setSelectedOutlet}
                                label="Outlet"
                                placeholder="Select Outlet..."
                                required
                            />
                        </div>

                        <div className="msr-filter-item">
                            <label className="msr-filter-label">Month</label>
                            <select
                                className="form-select"
                                value={month}
                                onChange={(e) => setMonth(Number(e.target.value))}
                            >
                                {MONTH_NAMES.map((m, idx) => (
                                    <option key={m} value={idx + 1}>{m}</option>
                                ))}
                            </select>
                        </div>

                        <div className="msr-filter-item">
                            <label className="msr-filter-label">Year</label>
                            <select
                                className="form-select"
                                value={year}
                                onChange={(e) => setYear(Number(e.target.value))}
                            >
                                {yearOptions.map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>

                        <div className="msr-filter-item msr-download-item">
                            <label className="msr-filter-label">&nbsp;</label>
                            <button
                                type="button"
                                className="msr-download-btn"
                                onClick={handleDownloadExcel}
                                disabled={!selectedOutlet || downloadBtnLoading}
                            >
                                {downloadBtnLoading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                    <>
                                        <i className="bi bi-file-earmark-spreadsheet"></i>
                                        Download Report
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="msr-body">

                        {!selectedOutlet && (
                            <div className="msr-empty-state">
                                <i className="bi bi-shop"></i>
                                <p>Choose an outlet above to load its monthly summary report.</p>
                            </div>
                        )}

                        {selectedOutlet && loading && (
                            <div className="msr-loading-state">
                                <div className="spinner-border" role="status" />
                                <p>Loading report…</p>
                            </div>
                        )}

                        {selectedOutlet && !loading && error && (
                            <div className="msr-error-state">
                                <i className="bi bi-exclamation-triangle"></i>
                                <p>{error}</p>
                            </div>
                        )}

                        {selectedOutlet && !loading && !error && summary && (
                            <>
                                {/* ─── Executive Summary ─── */}
                                <div className="msr-card msr-exec-summary">
                                    <h2>Executive Summary</h2>
                                    <p>
                                        For {MONTH_NAMES[month - 1]} {year}, <strong>{selectedOutlet.label}</strong> recorded{' '}
                                        <strong>{fmtNum(totalOrders)} total orders</strong> and
                                        generated final revenue of{' '}
                                        <strong className="msr-text-green">{fmtCurrency(grandTotal)}</strong>. Average order value
                                        was <strong>{fmtCurrency(aov)}</strong>, cancellation rate was{' '}
                                        <strong className="msr-text-red">{fmtPercent(cancellationRate)}</strong>, and coupon usage
                                        stood at <strong>{fmtPercent(couponUsageRate)}</strong> of all orders.
                                    </p>
                                </div>

                                {/* ─── Core KPIs ─── */}
                                <div className="msr-kpi-grid msr-kpi-grid-3">
                                    <KpiCard label="Total Orders" value={fmtNum(totalOrders)} 
                                    // sub={`${fmtNum(totalInvoices)} invoices`}
                                    sub={``}
                                    bg="#eaf8ef" border="#d7efdf" color="#159947" />
                                    <KpiCard label="Final Revenue Incl. Tax" value={fmtCurrency(grandTotal)} sub="Final billed amount" bg="#eef4ff" border="#d8e4ff" color="#2563eb" />
                                    <KpiCard label="Cancelled Orders" value={fmtNum(cancelledOrders)} sub={fmtCurrency(cancelledAmount)} bg="#fff1f2" border="#ffe1e6" color="#dc2626" />
                                </div>

                                <div className="msr-kpi-grid msr-kpi-grid-4">
                                    <KpiCard label="Average Order Value" value={fmtCurrency(aov)} sub="Revenue / Tot orders - Canc Orders" bg="#f0f9ff" border="#e0f2fe" color="#0284c7" />
                                    <KpiCard label="Cancellation Rate" value={fmtPercent(cancellationRate)} sub="Cancelled / total orders" bg="#fef2f2" border="#fee2e2" color="#dc2626" />
                                    <KpiCard label="Coupon Usage" value={fmtPercent(couponUsageRate)} sub={`${fmtNum(couponOrders)} of ${fmtNum(totalOrders)} orders`} bg="#f5f3ff" border="#ede9fe" color="#7c3aed" />
                                    <KpiCard label="Revenue Loss" value={fmtPercent(revenueLossPercent)} sub="Cancelled value / Revenue " bg="#fff7ed" border="#ffedd5" color="#ea580c" />
                                </div>

                                <div className="msr-kpi-grid msr-kpi-grid-3">
                                    <KpiCard label="Discount Given" value={fmtCurrency(discountAmount)} sub={`${fmtPercent(discountPercent)} of revenue`} bg="#fff7ed" border="#ffedd5" color="#ea580c" />
                                    <KpiCard label="Revenue (Taxable)" value={fmtCurrency(taxableAmount)} sub="Excluding GST" bg="#ecfdf3" border="#d5f5e2" color="#15803d" />
                                    <KpiCard label="Complimentary Orders" value={fmtNum(complimentaryOrders)} sub={fmtCurrency(complimentaryAmount)} bg="#fef2f2" border="#fee2e2" color="#ef4444" />
                                </div>

                                {/* ─── Order Source Performance ─── */}
                                <div className="msr-card">
                                    <h2>Order Source Performance</h2>
                                    <div className="msr-kpi-grid msr-kpi-grid-3">
                                        <KpiCard label="Kiosk Orders" value={fmtNum(kioskOrders)} sub={totalOrders ? `${fmtPercent((kioskOrders / totalOrders) * 100)} of orders` : ''} bg="#f0f9ff" border="#e0f2fe" color="#0284c7" />
                                        <KpiCard label="Online Platform Orders" value={fmtNum(onlineOrdersTotal)} sub={totalOrders ? `${fmtPercent((onlineOrdersTotal / totalOrders) * 100)} of orders` : ''} bg="#ecfeff" border="#cffafe" color="#0891b2" />
                                        <KpiCard label="App / Website Orders" value={fmtNum(otherOrders)} sub={totalOrders ? `${fmtPercent((otherOrders / totalOrders) * 100)} of orders` : ''} bg="#f5f3ff" border="#ede9fe" color="#7c3aed" />
                                    </div>
                                </div>

                                {/* ─── Online Platform Sales ─── */}
                                {onlinePlatforms?.length > 0 && (
                                    <div className="msr-card">
                                        <h2>Online Platform Sales</h2>
                                        <div className="msr-kpi-grid msr-kpi-grid-2">
                                            {onlinePlatforms?.map((p) => (
                                                <div key={p._id} className="msr-platform-card">
                                                    <div className="msr-platform-name">{p.platform}</div>
                                                    <div className="msr-platform-orders">{fmtNum(p.orders)} orders <span className='text-muted' style={{ fontSize: '13px' }}>out of {fmtNum(onlineOrdersTotal)} Orders</span></div>
                                                    <div className="msr-platform-row">Gross Revenue: <strong>{fmtCurrency(p.gross)}</strong></div>
                                                    <div className="msr-platform-row msr-text-red">Commission: - {fmtCurrency(p.commission)}</div>
                                                    <div className="msr-platform-row msr-text-green">Net Revenue: <strong>{fmtCurrency(p.net)}</strong></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ─── Food Delivery App Sales (Swiggy / Zomato) ───
                                     NOTE: sample payload had both swiggy & zomato as null,
                                     so this renders only once real data is present.
                                     Confirm the populated shape before relying on this block. */}
                                {hasFoodDeliveryData && (
                                    <div className="msr-card">
                                        <h2>Food Delivery App Sales</h2>
                                        <div className="msr-kpi-grid msr-kpi-grid-2">
                                            {foodDeliveryAppsSales.swiggy != null && (
                                                <div className="msr-platform-card">
                                                    <div className="msr-platform-name">Swiggy</div>
                                                    <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                                        {JSON.stringify(foodDeliveryAppsSales.swiggy, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                            {foodDeliveryAppsSales.zomato != null && (
                                                <div className="msr-platform-card">
                                                    <div className="msr-platform-name">Zomato</div>
                                                    <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                                        {JSON.stringify(foodDeliveryAppsSales.zomato, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ─── Category Performance ─── */}
                                {categories.length > 0 && (
                                    <div className="msr-card">
                                        <h2>Category Performance</h2>
                                        <div className="msr-table-scroll">
                                            <table className="msr-table">
                                                <thead>
                                                    <tr>
                                                        <th>Category</th>
                                                        <th className="text-end">Revenue</th>
                                                        <th className="text-end">Orders</th>
                                                        <th className="text-end">Share</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {categories.map((c) => {
                                                        const revenue = decimal(c.revenue);
                                                        const share = categoryRevenueTotal > 0 ? (revenue / categoryRevenueTotal) * 100 : 0;
                                                        return (
                                                            <tr key={c._id}>
                                                                <td>{c._id}</td>
                                                                <td className="text-end">{fmtCurrency(revenue)}</td>
                                                                <td className="text-end">{fmtNum(c.orders)}</td>
                                                                <td className="text-end">{fmtPercent(share)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ─── Category Toppers ─── */}
                                {categoryToppers.length > 0 && (
                                    <div className="msr-card">
                                        <h2>Category Toppers</h2>
                                        <div className="msr-kpi-grid msr-kpi-grid-4">
                                            {categoryToppers.map((p, i) => {
                                                const style = categoryStyle(p.classType);
                                                return (
                                                    <div key={`${p.classType}-${i}`} className="msr-topper-card" style={{ background: style.bg, borderColor: style.border }}>
                                                        <div className="msr-topper-label" style={{ color: style.text }}>
                                                            Top {p.classType.charAt(0) + p.classType.slice(1).toLowerCase()}
                                                        </div>
                                                        <div className="msr-topper-name">{p.product}</div>
                                                        <div className="msr-topper-sub">{fmtNum(p.totalSaleCount)} sold</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* ─── Product Performance ─── */}
                                <div className="msr-card">
                                    <h2>Product Performance</h2>
                                    <div className="msr-two-col">
                                        <div className="msr-product-box msr-product-box-top">
                                            <h3>Top 3 Selling Products</h3>
                                            <ul>
                                                {topProducts.map((p, i) => (
                                                    <li key={`${p.product}-${i}`}>
                                                        {['🥇', '🥈', '🥉'][i]} {p.product}
                                                        <span className="msr-product-count"> — {fmtNum(p.totalSaleCount)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="msr-product-box msr-product-box-bottom">
                                            <h3>Bottom 3 Selling Products</h3>
                                            <ul>
                                                {bottomProducts.map((p, i) => (
                                                    <li key={`${p.product}-${i}`}>
                                                        {i + 1}. {p.product}
                                                        <span className="msr-product-count"> — {fmtNum(p.totalSaleCount)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* ─── Coupon Usage ─── */}
                                {topCoupons.length > 0 && (
                                    <div className="msr-card">
                                        <h2>Top Coupons by Revenue</h2>
                                        <div className="msr-kpi-grid msr-kpi-grid-3">
                                            {topCoupons.map((c) => (
                                                <div key={c._id} className="msr-coupon-card">
                                                    <div className="msr-coupon-code">{c.code}</div>
                                                    <div className="msr-coupon-usage">{fmtNum(c.usage)} uses</div>
                                                    <div className="msr-coupon-row">Revenue: <strong>{fmtCurrency(decimal(c.revenue))}</strong></div>
                                                    <div className="msr-coupon-row msr-text-red">Discount: - {fmtCurrency(decimal(c.discount))}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ─── Tax Slab Details ─── */}
                                {taxSlabs.length > 0 && (
                                    <div className="msr-card">
                                        <h2>Tax Slab Details</h2>
                                        <div className="msr-table-scroll">
                                            <table className="msr-table">
                                                <thead>
                                                    <tr>
                                                        <th>Tax Slab</th>
                                                        <th className="text-end">CGST</th>
                                                        <th className="text-end">SGST</th>
                                                        <th className="text-end">Total Tax</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {taxSlabs.map((s) => (
                                                        <tr key={s.slab}>
                                                            <td>{s.slab}</td>
                                                            <td className="text-end">{fmtCurrency(s.cgst)}</td>
                                                            <td className="text-end">{fmtCurrency(s.sgst)}</td>
                                                            <td className="text-end">{fmtCurrency(s.cgst + s.sgst)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ─── Daily Sales Breakdown ─── */}
                                {reportData.length > 0 && (
                                    <div className="msr-card">
                                        <div className="msr-card-header-row">
                                            <h2>Daily Sales Breakdown</h2>
                                            <div className="msr-pagination">
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={page <= 1}
                                                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                                >
                                                    Prev
                                                </button>
                                                <span>Page {page}</span>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={reportData.length < limit}
                                                    onClick={() => setPage((p) => p + 1)}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                        <div className="msr-table-scroll">
                                            <table className="msr-table">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Invoices</th>
                                                        <th>Cancelled</th>
                                                        <th className="text-end">Taxable</th>
                                                        <th className="text-end">Discount</th>
                                                        <th className="text-end">Tax</th>
                                                        <th className="text-end">Grand Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.map((row) => (
                                                        <tr key={row.date}>
                                                            <td>{new Date(row.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                                            <td>{row.numberOfInvoices} ({row.invoiceStartNo}–{row.invoiceEndNo})</td>
                                                            <td className={row.cancelledOrderCount > 0 ? 'msr-text-red' : ''}>
                                                                {row.cancelledOrderCount} {row.cancelledOrderAmount ? `(${fmtCurrency(row.cancelledOrderAmount)})` : ''}
                                                            </td>
                                                            <td className="text-end">{row.totalTaxable != null ? fmtCurrency(row.totalTaxable) : '—'}</td>
                                                            <td className="text-end">{row.totalDiscount != null ? fmtCurrency(row.totalDiscount) : '—'}</td>
                                                            <td className="text-end">{row.totalTaxAmount != null ? fmtCurrency(row.totalTaxAmount) : '—'}</td>
                                                            <td className="text-end">{row.grandTotal != null ? fmtCurrency(row.grandTotal) : '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ─── Insights ─── */}
                                <div className="msr-card msr-insights">
                                    <h2>Insights</h2>
                                    <ul>
                                        <li>⚠️ Cancellation rate stood at <strong className="msr-text-red">{fmtPercent(cancellationRate)}</strong>, costing an estimated <strong>{fmtCurrency(cancelledAmount)}</strong> in lost revenue.</li>
                                        <li>🎟️ Coupons were used on <strong>{fmtPercent(couponUsageRate)}</strong> of orders ({fmtNum(couponOrders)} of {fmtNum(totalOrders)}).</li>
                                        {onlineOrdersTotal > 0 && (
                                            <li>🚚 Online platforms contributed <strong>{fmtNum(onlineOrdersTotal)}</strong> orders this month.</li>
                                        )}
                                        {complimentaryOrders > 0 && (
                                            <li>🎁 <strong>{fmtNum(complimentaryOrders)}</strong> complimentary orders were issued, worth {fmtCurrency(complimentaryAmount)}.</li>
                                        )}
                                        {categories.length > 0 && (
                                            <li>🏆 Highest revenue category: <strong>{[...categories].sort((a, b) => decimal(b.revenue) - decimal(a.revenue))[0]._id}</strong>.</li>
                                        )}
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default MonthSummaryReport;





// Old Month Summary Report

// import React, { useState, useEffect } from "react";
// import axiosInstance from '../../api/axios';
// import { toast } from "react-toastify";
// import { 
//   Button,
//   Col,
//   Form,
//   Row,
//   Table,
//   Spinner,
//   Pagination,
//   Badge,
//   InputGroup,
//   Modal
// } from "react-bootstrap";
// import DatePicker from "react-datepicker";
// import { Search, Printer, File, FileExcel, FileEarmarkExcel, FileEaselFill, FileExcelFill, Download, FileEarmarkSpreadsheet, ArrowLeft } from 'react-bootstrap-icons';
// import Header from '../../layouts/Header';
// import { useNavigate } from "react-router-dom";
// import OutletSelect from "../../components/OutletSelect";

// const MonthSummaryReport = () => {

//   const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
//   const [skin, setSkin] = useState(currentSkin);
//   const navigate = useNavigate();
  
//   const accessToken = localStorage.getItem("authToken");
//   const [selectedOutlet, setSelectedOutlet] = useState("");
//   const [selectedOutletName, setSelectedOutletName] = useState('');
//   const [selectedMonth, setSelectedMonth] = useState(new Date());
//   const [loading, setLoading] = useState(false);
//   const [subBtnLoading, setSubBtnLoading] = useState(false);
//   const [printBtnLoading, setPrintBtnLoading] = useState(false);
//   const [outlets, setOutlets] = useState([]);
//   const [monthReportList, setMonthReportList] = useState([]);
//   const [outletName, setOutletName] = useState('');
//   const [clicked, setClicked] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [recordsPerPage, setRecordsPerPage] = useState(50);
//   const [totalPages, setTotalPages] = useState(1);
//   const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
  
//   // New state for dynamic tax percentages
//   const [taxPercentages, setTaxPercentages] = useState([]);

//   useEffect(() => {
//     getOutlets();
//   }, []);

//   const handleDownloadExcel = async () => {
//     setDownloadBtnLoading(true);
//     getMonthReport(selectedMonth, selectedOutlet, currentPage, recordsPerPage, true);
//   };

//   const getMonthReport = async (monthDate, outlet, page = 1, limit = recordsPerPage, download) => {
//     setLoading(true);
//     try {
//       const headers = {
//         Authorization: `Bearer ${accessToken}`,
//         "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
//       };
      
//       const month = monthDate.getMonth() + 1;
//       const year = monthDate.getFullYear();

//       const allMonths = [
//         "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
//       ]

//       let newUrl = `order/monthlyOrderSummary?outletId=${outlet.value}&year=${year}&month=${month}&page=${page}&limit=${limit}`

//       if(download) {
//         newUrl += `&isDownload=${download}`
//       }
//       const response = await axiosInstance.get(newUrl,
//         { 
//           headers, 
//           responseType: download ? 'blob' : 'json'
//         }
//       );

//       if (download) {
//         // Download logic remains the same...
//         const blob = new Blob([response.data], { 
//             type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
//         });
        
//         const link = document.createElement('a');
//         link.href = window.URL.createObjectURL(blob);
        
//         const fileName = `[${outlet?.label}] Summary Report ${allMonths[month - 1]}.xlsx`;
//         link.download = fileName;
        
//         document.body.appendChild(link);
//         link.click();
//         document.body.removeChild(link);
//         window.URL.revokeObjectURL(link.href);

//         toast.success('Excel file downloaded successfully!', {
//             position: "top-right",
//             autoClose: 3000,
//             theme: "light"
//         });
//       }

//       if (response?.data?.status === true && !download) {
//         setMonthReportList(response?.data?.data?.reportData);
//         setTotalPages(response?.data?.data?.pagination?.pages || 1);
        
//         // Extract unique tax percentages from the response
//         const allTaxPercentages = extractTaxPercentages(response?.data?.data?.reportData);
//         setTaxPercentages(allTaxPercentages);
        
//         toast.success("Data Fetched Successfully", {
//           position: "top-right",
//           autoClose: 3000,
//           theme: "light",
//         });
//       }
//     } catch (error) {
//       handleApiError(error);
//     } finally {
//       setLoading(false);
//       setDownloadBtnLoading(false);
//     }
//   };

//   // Function to extract unique tax percentages from the report data
//   const extractTaxPercentages = (reportData) => {
//     if (!reportData || !Array.isArray(reportData)) return [];
    
//     const percentages = new Set();
    
//     reportData.forEach(report => {
//       if (report?.taxDetails && Array.isArray(report.taxDetails)) {
//         report.taxDetails.forEach(tax => {
//           if (tax.taxPercent) {
//             percentages.add(tax.taxPercent);
//           }
//         });
//       }
//     });
    
//     // Convert Set to array and sort
//     return Array.from(percentages).sort((a, b) => a - b);
//   };

//   // Function to get CGST/SGST percentage for a given tax percentage
//   const getGSTPercentage = (taxPercent) => {
//     return (taxPercent / 2).toFixed(1);
//   };

//   // Function to render dynamic tax column headers
//   const renderTaxColumnHeaders = () => {
//     return taxPercentages.map(percent => (
//       <React.Fragment key={percent}>
//         <th className="text-end">TAXABLE ({percent}%)</th>
//         <th className="text-end">CGST ({getGSTPercentage(percent)}%)</th>
//         <th className="text-end">SGST ({getGSTPercentage(percent)}%)</th>
//       </React.Fragment>
//     ));
//   };

//   // Function to render tax data for each row
//   const renderTaxData = (report) => {
//     return taxPercentages.map(percent => {
//       const taxForPercent = report?.taxDetails?.filter(t => t.taxPercent === percent) || [];
//       const totalTaxable = taxForPercent.reduce((acc, curr) => acc + (curr?.taxable || 0), 0);
//       const totalCGST = taxForPercent.reduce((acc, curr) => acc + (curr?.CGST || 0), 0);
//       const totalSGST = taxForPercent.reduce((acc, curr) => acc + (curr?.SGST || 0), 0);
      
//       return (
//         <React.Fragment key={percent}>
//           <td className="text-end">₹ {totalTaxable.toFixed(2)}</td>
//           <td className="text-end">₹ {totalCGST.toFixed(2)}</td>
//           <td className="text-end">₹ {totalSGST.toFixed(2)}</td>
//         </React.Fragment>
//       );
//     });
//   };

//   // Function to render grand total tax data
//   const renderGrandTotalTaxData = () => {
//     return taxPercentages.map(percent => {
//       const totalTaxable = monthReportList.reduce((acc, report) => 
//         acc + (report?.taxDetails?.filter(t => t.taxPercent === percent)
//           .reduce((sum, tax) => sum + (tax.taxable || 0), 0) || 0), 0);
      
//       const totalCGST = monthReportList.reduce((acc, report) => 
//         acc + (report?.taxDetails?.filter(t => t.taxPercent === percent)
//           .reduce((sum, tax) => sum + (tax.CGST || 0), 0) || 0), 0);
      
//       const totalSGST = monthReportList.reduce((acc, report) => 
//         acc + (report?.taxDetails?.filter(t => t.taxPercent === percent)
//           .reduce((sum, tax) => sum + (tax.SGST || 0), 0) || 0), 0);
      
//       return (
//         <React.Fragment key={percent}>
//           <td className="text-end fw-bold">₹ {totalTaxable.toFixed(2)}</td>
//           <td className="text-end fw-bold">₹ {totalCGST.toFixed(2)}</td>
//           <td className="text-end fw-bold">₹ {totalSGST.toFixed(2)}</td>
//         </React.Fragment>
//       );
//     });
//   };

//   // Update the print function to handle dynamic tax columns
//   const handlePrintReport = () => {
//     // Calculate totals for print
//     let totalInv = 0;
//     let totalCancelledInv = 0;
//     let totalDiscountAmt = 0;
//     let grandTotalCancelledAmount = 0;
//     let grandTotalTaxable = 0;
//     let grandTotal = 0;

//     // Calculate tax totals for print
//     const taxTotals = {};
//     taxPercentages.forEach(percent => {
//       taxTotals[percent] = {
//         taxable: 0,
//         cgst: 0,
//         sgst: 0
//       };
//     });

//     const rowsHtml = monthReportList?.length > 0
//       ? monthReportList.map((report) => {
//           totalInv += report?.numberOfInvoices || 0;
//           totalCancelledInv += report?.cancelledOrderCount || 0;
//           totalDiscountAmt += report?.totalDiscount || 0;
//           grandTotalCancelledAmount += report?.cancelledOrderAmount || 0;
//           grandTotalTaxable += report?.totalTaxable || 0;
//           grandTotal += report?.grandTotal || 0;

//           // Calculate tax amounts for this row
//           const rowTaxHtml = taxPercentages.map(percent => {
//             const taxForPercent = report?.taxDetails?.filter(t => t.taxPercent === percent) || [];
//             const taxable = taxForPercent.reduce((acc, curr) => acc + (curr?.taxable || 0), 0);
//             const cgst = taxForPercent.reduce((acc, curr) => acc + (curr?.CGST || 0), 0);
//             const sgst = taxForPercent.reduce((acc, curr) => acc + (curr?.SGST || 0), 0);
            
//             // Add to totals
//             taxTotals[percent].taxable += taxable;
//             taxTotals[percent].cgst += cgst;
//             taxTotals[percent].sgst += sgst;
            
//             return `
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${taxable.toFixed(2)}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${cgst.toFixed(2)}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${sgst.toFixed(2)}</td>
//             `;
//           }).join("");

//           return `
//             <tr style="font-size: 11px">
//               <td style="border: 1px solid #dee2e6; padding: 8px;">${report?.date}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px;">${report?.invoiceStartNo}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px;">${report?.invoiceEndNo}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center">${report?.numberOfInvoices}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center">${report?.cancelledOrderCount}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.totalDiscount?.toFixed(2)}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.cancelledOrderAmount?.toFixed(2)}</td>
//               ${rowTaxHtml}
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.totalTaxable?.toFixed(2)}</td>
//               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.grandTotal?.toFixed(2)}</td>
//             </tr>
//           `;
//         }).join("")
//       : `<tr><td colspan="${13 + (taxPercentages.length * 3)}" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">No Reports Found</td></tr>`;

//     // Generate tax headers for print
//     const taxHeadersHtml = taxPercentages.map(percent => `
//       <th style="border: 1px solid #dee2e6; padding: 8px; text-align: end">TAXABLE (${percent}%)</th>
//       <th style="border: 1px solid #dee2e6; padding: 8px; text-align: end">CGST (${getGSTPercentage(percent)}%)</th>
//       <th style="border: 1px solid #dee2e6; padding: 8px; text-align: end">SGST (${getGSTPercentage(percent)}%)</th>
//     `).join("");

//     // Generate tax totals for print
//     const taxTotalsHtml = taxPercentages.map(percent => `
//       <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end; font-weight: bold">₹ ${taxTotals[percent].taxable.toFixed(2)}</td>
//       <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end; font-weight: bold">₹ ${taxTotals[percent].cgst.toFixed(2)}</td>
//       <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end; font-weight: bold">₹ ${taxTotals[percent].sgst.toFixed(2)}</td>
//     `).join("");

//     const formattedMonth = selectedMonth.toLocaleString("en-US", {
//       month: "long",
//       year: "numeric",
//     });

//     const win = window.open("", "_blank");
//     win.document.write(`
//       <html>
//         <head>
//           <title>Monthly Summary Report</title>
//           <style>
//             body { font-family: Arial, sans-serif; margin: 20px; }
//             .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
//             .title { font-size: 18px; font-weight: bold; }
//             .date-range { font-size: 14px; color: #666; }
//             table { width: 100%; border-collapse: collapse; font-size: 12px; }
//             th { background-color: #f8f9fa; text-align: left; border: 1px solid #dee2e6; padding: 8px; }
//             td { border: 1px solid #dee2e6; padding: 8px; }
//             .text-center { text-align: center; }
//             .text-end { text-align: right; }
//             .total-row { font-weight: bold; }
//           </style>
//         </head>
//         <body>
//           <div class="header">
//             <div class="title">Monthly Summary Report</div>
//             <div class="date-range">${formattedMonth} - ${outletName}</div>
//           </div>
//           <table>
//             <thead>
//               <tr>
//                 <th>DATE</th>
//                 <th>INV. START NO</th>
//                 <th>INV. END NO</th>
//                 <th class="text-center">TOT. INV.</th>
//                 <th class="text-center">CANC. INV.</th>
//                 <th class="text-end">DIS. AMT</th>
//                 <th class="text-end">CANC. AMT</th>
//                 ${taxHeadersHtml}
//                 <th class="text-end">TOT. TAXABLE</th>
//                 <th class="text-end">GRAND TOTAL</th>
//               </tr>
//             </thead>
//             <tbody>
//               ${rowsHtml}
//               <tr class="total-row">
//                 <td colspan="3" style="text-align: right;">GRAND TOTAL:</td>
//                 <td class="text-center">${totalInv}</td>
//                 <td class="text-center">${totalCancelledInv}</td>
//                 <td class="text-end">₹ ${totalDiscountAmt.toFixed(2)}</td>
//                 <td class="text-end">₹ ${grandTotalCancelledAmount.toFixed(2)}</td>
//                 ${taxTotalsHtml}
//                 <td class="text-end">₹ ${grandTotalTaxable.toFixed(2)}</td>
//                 <td class="text-end">₹ ${grandTotal.toFixed(2)}</td>
//               </tr>
//             </tbody>
//           </table>
//           <script>
//             setTimeout(() => {
//               window.print();
//               window.close();
//             }, 500);
//           </script>
//         </body>
//       </html>
//     `);
//     win.document.close();
//   };

//   // ... rest of your existing functions (getOutlets, handleApiError, handleSubmit, getOutletName, handlePageChange, renderPaginationItems) remain the same

//   const getOutlets = async () => {
//     try {
//       const headers = {
//         Authorization: `Bearer ${accessToken}`,
//       };
//       const response = await axiosInstance.get("outlet", { headers });

//       if (response?.data?.status === true) {
//         const activeOutlets = response?.data?.data?.outlets.filter(
//           (active) => active?.status === "ACTIVE" || "INACTIVE"
//         );
//         setOutlets(activeOutlets);
//       }
//     } catch (error) {
//       handleApiError(error);
//     }
//   };

//   const handleApiError = (error) => {
//     if (error?.response) {
//       let errorMessage = "";
//       if (error.response?.status === 406) {
//         errorMessage =
//           error?.response?.data?.error?.message ||
//           error?.response?.data?.error;
//       } else if (error.response?.status === 412) {
//         errorMessage = error?.response?.data?.data?.message;
//       } else if (error.response?.status === 500) {
//         errorMessage = error?.response?.data?.error;
//       }

//       toast.error(errorMessage, {
//         position: "top-right",
//         autoClose: 3000,
//         theme: "colored",
//       });
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setSubBtnLoading(true);
//     setClicked(true);
//     setCurrentPage(1);
//     await getMonthReport(selectedMonth, selectedOutlet);
//     setSubBtnLoading(false);
//   };

//   const getOutletName = (e) => {   
//     const value = outlets?.filter(val => val._id === e);
//     setOutletName(value[0]?.name || '');  
//   };

//   const handlePageChange = (page) => {
//     if (page > 0 && page <= totalPages) {
//       setCurrentPage(page);
//       getMonthReport(selectedMonth, selectedOutlet, page, recordsPerPage);
//     }
//   };

//   const renderPaginationItems = () => {
//     const items = [];
//     const maxVisiblePages = 5;
//     let startPage, endPage;

//     if (totalPages <= maxVisiblePages) {
//       startPage = 1;
//       endPage = totalPages;
//     } else {
//       const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
//       const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;
      
//       if (currentPage <= maxVisibleBeforeCurrent) {
//         startPage = 1;
//         endPage = maxVisiblePages;
//       } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
//         startPage = totalPages - maxVisiblePages + 1;
//         endPage = totalPages;
//       } else {
//         startPage = currentPage - maxVisibleBeforeCurrent;
//         endPage = currentPage + maxVisibleAfterCurrent;
//       }
//     }

//     if (startPage > 1) {
//       items.push(
//         <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
//           1
//         </Pagination.Item>
//       );
//       if (startPage > 2) {
//         items.push(<Pagination.Ellipsis key="ellipsis-start" />);
//       }
//     }

//     for (let i = startPage; i <= endPage; i++) {
//       items.push(
//         <Pagination.Item 
//           key={i} 
//           active={i === currentPage}
//           onClick={() => handlePageChange(i)}
//         >
//           {i}
//         </Pagination.Item>
//       );
//     }

//     if (endPage < totalPages) {
//       if (endPage < totalPages - 1) {
//         items.push(<Pagination.Ellipsis key="ellipsis-end" />);
//       }
//       items.push(
//         <Pagination.Item 
//           key={totalPages} 
//           onClick={() => handlePageChange(totalPages)}
//         >
//           {totalPages}
//         </Pagination.Item>
//       );
//     }

//     return items;
//   };

//   const formatMonth = selectedMonth
//     .toLocaleString("en-US", { month: "long", year: "numeric" })
//     .toUpperCase();

//   return (
//     <React.Fragment>
//       <Header onSkin={setSkin} />
//       <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
//         <div className="card shadow-sm border-0">
//           <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
//             <div>
//                 <h4 className="mb-0 fw-bold d-inline-block">
//                     <i className="bi bi-calendar-month me-2"></i>MONTHLY SUMMARY REPORT
//                 </h4>
//             </div>
//             <div>
//                 <Button 
//                     variant="outline-secondary"
//                     size="sm" 
//                     onClick={() => navigate('/reports/all-reports')}
//                     className="me-3 d-flex align-items-center fw-semibold"
//                     style={{ height: '38px' }}
//                 >
//                     <ArrowLeft className="me-1" /> Back to Reports
//                 </Button>
//             </div>
//           </div>
//           <hr className='mt-0 mb-0' /> 
          
//           <div className="card-body">
//             {/* Filters Section */}
//             <div className="p-3 bg-light rounded-3">
//               <Form onSubmit={handleSubmit}>
//                 <Row className="g-3">
//                   <Col md={4}>
//                     <Form.Group controlId="outletSelect">
//                       <OutletSelect
//                         value={selectedOutlet}
//                         onChange={(option) => setSelectedOutlet(option)}
//                         required={true}
//                       />
//                     </Form.Group>
//                   </Col>

//                   <Col md={2}>
//                     <Form.Group controlId="monthSelect">
//                       <Form.Label className="fw-semibold">
//                         <i className="bi bi-calendar me-2"></i>Select Month
//                       </Form.Label>
//                       <DatePicker
//                         selected={selectedMonth}
//                         onChange={(date) => setSelectedMonth(date)}
//                         dateFormat="MMM yyyy"
//                         showMonthYearPicker
//                         className="form-control border-secondary"
//                       />
//                     </Form.Group>
//                   </Col>

//                   <Col md={3} className="d-flex align-items-end">
//                     <Button
//                       variant="primary"
//                       type="submit"
//                       className="w-100"
//                       disabled={!selectedOutlet || !selectedMonth}
//                     >
//                       {subBtnLoading ? (
//                         <Spinner size="sm" animation="border" />
//                       ) : (
//                         "Generate Report"
//                       )}
//                     </Button>
//                   </Col>

//                   <Col className="d-flex align-items-end">
//                       <Button
//                         variant="outline-success"
//                         className="me-3 w-100 fw-semibold"
//                         onClick={handleDownloadExcel}
//                         disabled={!monthReportList?.length}
//                       >
//                         <FileEarmarkSpreadsheet className="me-2" />
//                         { downloadBtnLoading ? <Spinner size="sm" /> : 'Download Excel' }
//                       </Button>
//                   </Col>
//                 </Row>
//               </Form>
//             </div>

//             {/* Results Section */}
//             {!clicked ? (
//               <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
//                 <div className="text-center">
//                   <i className="bi bi-clipboard-data fs-1 text-muted"></i>
//                   <h5 className="text-muted mt-3">
//                     Please select an outlet and month to view the monthly summary
//                   </h5>
//                 </div>
//               </div>
//             ) : (
//               <>
//                 {/* Pagination and Records Per Page - Top */}
//                 <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
//                     <div className="d-flex align-items-center">
//                         <span className="me-2 text-muted">Show</span>
//                         <Form.Select
//                           style={{ width: 'auto' }}
//                           value={recordsPerPage}
//                           onChange={(e) => {
//                               setRecordsPerPage(Number(e.target.value));
//                               setCurrentPage(1);
//                               getMonthReport(selectedMonth, selectedOutlet, 1, Number(e.target.value));
//                           }}
//                           className="border-secondary"
//                         >
//                           <option value={50}>50</option>
//                           <option value={100}>100</option>
//                           <option value={200}>200</option>
//                           <option value={300}>300</option>
//                           <option value={500}>500</option>
//                         </Form.Select>
//                         <span className="ms-2 text-muted">entries</span>
//                     </div>
                  
//                     <div className="d-flex align-items-center">
//                         <Pagination className="mb-0">
//                             <Pagination.Prev
//                                 disabled={currentPage === 1}
//                                 onClick={() => handlePageChange(currentPage - 1)}
//                             />
//                             {renderPaginationItems()}
//                             <Pagination.Next
//                                 disabled={currentPage === totalPages}
//                                 onClick={() => handlePageChange(currentPage + 1)}
//                             />
//                         </Pagination>
//                   </div>
//                 </div>

//                 {/* Monthly Summary Table */}
//                 <div className="table-responsive border rounded-3">
//                   <Table striped bordered hover className="mb-0">
//                     <thead className="bg-light">
//                       <tr>
//                         <th>DATE</th>
//                         <th>INV. START NO</th>
//                         <th>INV. END NO</th>
//                         <th className="text-center">TOT. INV.</th>
//                         <th className="text-center">CANC. INV.</th>
//                         <th className="text-end">DIS. AMT</th>
//                         <th className="text-end">CANC. AMT</th>
                        
//                         {/* Dynamic Tax Column Headers */}
//                         {renderTaxColumnHeaders()}
                        
//                         <th className="text-end">TOT. TAXABLE</th>
//                         <th className="text-end">GRAND TOTAL</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {loading ? (
//                         <tr>
//                           <td colSpan={9 + (taxPercentages.length * 3)} className="text-center py-4">
//                             <Spinner animation="border" variant="primary" />
//                             <p className="mt-2 mb-0">Loading monthly summary...</p>
//                           </td>
//                         </tr>
//                       ) : monthReportList?.length > 0 ? (
//                         <>
//                           {monthReportList.map((report, index) => (
//                             <tr key={index}>
//                               <td>{report?.date}</td>
//                               <td>{report?.invoiceStartNo}</td>
//                               <td>{report?.invoiceEndNo}</td>
//                               <td className="text-center">{report?.numberOfInvoices}</td>
//                               <td className="text-center text-danger">{report?.cancelledOrderCount}</td>
//                               <td className="text-end">₹ {report?.totalDiscount?.toFixed(2)}</td>
//                               <td className="text-end text-danger">₹ {report?.cancelledOrderAmount?.toFixed(2)}</td>
                              
//                               {/* Dynamic Tax Data */}
//                               {renderTaxData(report)}
                              
//                               <td className="text-end">₹ {report?.totalTaxable?.toFixed(2)}</td>
//                               <td className="text-end fw-semibold text-success">
//                                 ₹ {report?.grandTotal?.toFixed(2)}
//                               </td>
//                             </tr>
//                           ))}
//                           <tr className="bg-light">
//                             <td colSpan="3" className="text-end fw-bold">GRAND TOTAL:</td>
//                             <td className="text-center fw-bold">
//                               {monthReportList.reduce((acc, report) => acc + (report?.numberOfInvoices || 0), 0)}
//                             </td>
//                             <td className="text-center fw-bold text-danger">
//                               {monthReportList.reduce((acc, report) => acc + (report?.cancelledOrderCount || 0), 0)}
//                             </td>
//                             <td className="text-end fw-bold">
//                               ₹ {monthReportList.reduce((acc, report) => acc + (report?.totalDiscount || 0), 0).toFixed(2)}
//                             </td>
//                             <td className="text-end fw-bold text-danger">
//                               ₹ {monthReportList.reduce((acc, report) => acc + (report?.cancelledOrderAmount || 0), 0).toFixed(2)}
//                             </td>
                            
//                             {/* Dynamic Grand Total Tax Data */}
//                             {renderGrandTotalTaxData()}
                            
//                             <td className="text-end fw-bold">
//                               ₹ {monthReportList.reduce((acc, report) => acc + (report?.totalTaxable || 0), 0).toFixed(2)}
//                             </td>
//                             <td className="text-end fw-bold text-success">
//                               ₹ {monthReportList.reduce((acc, report) => acc + (report?.grandTotal || 0), 0).toFixed(2)}
//                             </td>
//                           </tr>
//                         </>
//                       ) : (
//                         <tr>
//                           <td colSpan={8 + (taxPercentages.length * 3)} className="text-center py-4">
//                             <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
//                             <p className="mt-2 mb-0 text-muted">
//                               No data found for the selected month and outlet
//                             </p>
//                           </td>
//                         </tr>
//                       )}
//                     </tbody>
//                   </Table>
//                 </div>

//                 {/* Pagination and Records Per Page - Bottom */}
//                 <div className="d-flex justify-content-between align-items-center mt-3">
//                   <div className="text-muted">
//                     Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
//                     {Math.min(currentPage * recordsPerPage, monthReportList?.length)} of{' '}
//                     {monthReportList?.length} entries
//                   </div>
//                   <Pagination>
//                     <Pagination.Prev
//                       disabled={currentPage === 1}
//                       onClick={() => handlePageChange(currentPage - 1)}
//                     />
//                     {renderPaginationItems()}
//                     <Pagination.Next
//                       disabled={currentPage === totalPages}
//                       onClick={() => handlePageChange(currentPage + 1)}
//                     />
//                   </Pagination>
//                 </div>
//               </>
//             )}
//           </div>
//         </div>
//       </div>
//     </React.Fragment>
//   );
// };

// export default MonthSummaryReport;